import { useId } from 'react'

import {
  CAFFEINE_LAST_LABELS,
  NUMERIC_FIELDS,
  type DraftField,
  type JournalCaffeineLast,
  type SelfReportDraft,
} from '../../lib/selfReport'
import { NumberStepper } from '../ui'
import { ChoiceChips } from './ChoiceChips'

const LAST_OPTIONS = (Object.keys(CAFFEINE_LAST_LABELS) as JournalCaffeineLast[]).map((value) => ({
  value,
  label: CAFFEINE_LAST_LABELS[value],
}))
const CUPS = NUMERIC_FIELDS.caffeine_cups

interface CaffeineFieldsProps {
  draft: SelfReportDraft
  set: <K extends DraftField>(field: K, value: SelfReportDraft[K]) => void
  cupsError: string | null
}

/**
 * The Caffeine block, shared by both journal forms — `ExerciseFields`' sibling.
 *
 * Sprint 1.9.11 asks a second caffeine question, "Last cup", because a cup count
 * alone says nothing about whether any of it was before this recording. Two
 * fields about one thing is the shape that produced JOURNAL-FIX-01, so this is
 * one component from the start rather than two copies waiting to drift.
 *
 * ── THE LOCK IS NOT THE EXERCISE LOCK, AND THE DIFFERENCE IS THE POINT ──────
 *
 * "Exercised: No" leaves nothing that could have finished, so Finished is
 * disabled and a held value can only be cleared. **Zero cups is not that.** It
 * has a true answer — `none`, "no coffee yet today" — and the vocabulary carries
 * it as a real member precisely so it can be stored. Null still means nobody
 * said, and BSV-010 §4 requires the two to stay distinguishable.
 *
 * So at `0` the control is pinned to `none` rather than emptied: it reads as
 * answered because it is. Nothing is deleted — a previously stated "under an
 * hour ago" is replaced by the answer the person just gave, in the same gesture,
 * on screen. The note says what happened, because a value changing under a
 * person is worth a sentence even when the change is right.
 *
 * Clearing the cup count entirely withdraws the question, and `none` goes with
 * it — but only `none`, never an answer they typed themselves.
 */
export function CaffeineFields({ draft, set, cupsError }: CaffeineFieldsProps) {
  const noteId = useId()
  const zero = draft.caffeine_cups === '0'
  return (
    <>
      <div className="journal__group journal__group--grid">
        <NumberStepper
          label={CUPS.label}
          value={draft.caffeine_cups}
          onChange={(next) => {
            set('caffeine_cups', next)
            // The pin, written where the count changes rather than in a render
            // effect: an effect that rewrites a sibling field is the shape that
            // makes "who cleared my answer?" unanswerable.
            if (next === '0') set('caffeine_last', 'none')
            else if (next === '' && draft.caffeine_last === 'none') set('caffeine_last', null)
          }}
          min={CUPS.min}
          max={'max' in CUPS ? (CUPS as { max: number }).max : undefined}
          step={CUPS.step}
          unit={CUPS.unit}
          error={cupsError}
        />
        <ChoiceChips
          label="Last cup"
          options={LAST_OPTIONS}
          value={draft.caffeine_last}
          onChange={(next) => set('caffeine_last', next as JournalCaffeineLast | null)}
          disabled={zero}
          describedBy={zero ? noteId : undefined}
        />
      </div>
      {/*
        Always mounted, empty until it has something to say — a region inserted
        together with its content is announced unreliably (src/test/liveRegions.test.ts).
      */}
      <div className="journal__note-region" role="status">
        {zero && (
          <p className="journal__note" id={noteId}>
            No cups, so there is no last cup. Enter a count to answer it.
          </p>
        )}
      </div>
    </>
  )
}
